import { jsx, jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from "react";
import { DndContext, MouseSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Checkbox, Classes } from "@blueprintjs/core";
import InfoHelper from "../InfoHelper";
const DraggableColumnOption = ({
  field,
  index,
  onVisibilityChange,
  numVisible
}) => {
  const { displayName, isHidden, path, subFrag, immovable, type } = field;
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: path,
    disabled: immovable === "true"
  });
  if (type === "action") {
    return null;
  }
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "grab",
    marginBottom: 5
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref: setNodeRef,
      style,
      className: "SortableItem",
      "data-path": path,
      ...attributes,
      ...listeners,
      children: /* @__PURE__ */ jsx(
        Checkbox,
        {
          name: `${path}-${index}`,
          onChange: () => {
            if (numVisible <= 1 && !isHidden) {
              return window.toastr.warning(
                "We have to display at least one column :)"
              );
            }
            onVisibilityChange({ shouldShow: isHidden, path });
          },
          checked: !isHidden,
          label: /* @__PURE__ */ jsxs("span", { style: { display: "flex", marginTop: -17 }, children: [
            displayName,
            " ",
            field.fieldGroup && /* @__PURE__ */ jsxs(
              "span",
              {
                style: { fontSize: 10, marginLeft: 5, marginTop: 2 },
                className: Classes.TEXT_MUTED,
                children: [
                  "(",
                  field.fieldGroup,
                  ")"
                ]
              }
            ),
            subFrag && /* @__PURE__ */ jsx(InfoHelper, { style: { marginLeft: 5 }, children: "Note: Viewing this complex column may cause the table to load slower. Try hiding it for better performance." })
          ] })
        },
        index
      )
    }
  );
};
const DraggableColumnOptions = ({
  fields,
  onVisibilityChange,
  moveColumnPersist,
  numVisible
}) => {
  const [sortedFields, setSortedFields] = useState(fields);
  useEffect(() => {
    setSortedFields(fields);
  }, [fields]);
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 5
    }
  });
  const sensors = useSensors(mouseSensor);
  const handleDragStart = (event) => {
    document.body.classList.add("column-dragging");
    const { active } = event;
    if (active) {
      const activeNode = document.querySelector(`[data-path="${active.id}"]`);
      if (activeNode) {
        activeNode.classList.add("dragging");
      }
    }
  };
  const handleDragEnd = (event) => {
    document.body.classList.remove("column-dragging");
    const draggingItem = document.querySelector(".SortableItem.dragging");
    if (draggingItem) {
      draggingItem.classList.remove("dragging");
    }
    const { active, over } = event;
    if (!over || !active || active.id === over.id) {
      return;
    }
    const oldIndex = sortedFields.findIndex((f) => f.path === active.id);
    const newIndex = sortedFields.findIndex((f) => f.path === over.id);
    const newSortedFields = arrayMove(sortedFields, oldIndex, newIndex);
    setSortedFields(newSortedFields);
    moveColumnPersist({ oldIndex, newIndex });
  };
  return /* @__PURE__ */ jsx(
    DndContext,
    {
      sensors,
      modifiers: [restrictToVerticalAxis],
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd,
      children: /* @__PURE__ */ jsx(
        SortableContext,
        {
          items: sortedFields.map((field) => field.path),
          strategy: verticalListSortingStrategy,
          children: /* @__PURE__ */ jsx("div", { children: sortedFields.map((field, index) => /* @__PURE__ */ jsx(
            DraggableColumnOption,
            {
              field,
              index,
              onVisibilityChange,
              numVisible
            },
            field.path || index
          )) })
        }
      )
    }
  );
};
export default DraggableColumnOptions;
